import { useState, useEffect, useCallback } from "react";
import { 
  BarChart3, Users, DollarSign, TrendingUp, Briefcase, 
  FileText, Clock, Award, ChevronRight, RefreshCw, Download,
  CheckCircle, XCircle, AlertCircle, Percent
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

export default function DashboardTab() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentCommissions, setAgentCommissions] = useState(null);
  const [showCommissionsModal, setShowCommissionsModal] = useState(false);
  
  // Totals
  const [totals, setTotals] = useState({
    totalAgents: 0,
    totalClients: 0,
    totalTramites: 0,
    totalCommissions: 0,
    pendingCommissions: 0
  });

  const fetchActivityData = useCallback(async () => {
    try {
      const [activityRes, commissionsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/activity`, axCfg),
        axios.get(`${API}/admin/commissions`, axCfg)
      ]);
      
      setActivityData(activityRes.data);
      setCommissions(commissionsRes.data);
      
      // Calculate totals
      const agents = activityRes.data;
      const totalClients = agents.reduce((sum, a) => sum + (a.stats?.clients_created || 0), 0);
      const totalTramites = agents.reduce((sum, a) => sum + (a.stats?.tramites_count || 0), 0);
      const totalCommissions = agents.reduce((sum, a) => sum + (a.stats?.total_commissions || 0), 0);
      const pendingCommissions = agents.reduce((sum, a) => sum + (a.stats?.pending_commissions || 0), 0);
      
      setTotals({
        totalAgents: agents.length,
        totalClients,
        totalTramites,
        totalCommissions,
        pendingCommissions
      });
      
    } catch (e) {
      console.error("Error loading dashboard:", e);
      toast.error("Error al cargar dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchActivityData();
  };

  const openCommissionsModal = async (agent) => {
    setSelectedAgent(agent);
    setShowCommissionsModal(true);
    
    try {
      const res = await axios.get(`${API}/admin/agents/${agent.id}/commissions`, axCfg);
      setAgentCommissions(res.data);
    } catch (e) {
      toast.error("Error al cargar comisiones del agente");
    }
  };

  const markCommissionPaid = async (commissionId) => {
    try {
      await axios.patch(`${API}/admin/commissions/${commissionId}/pay`, {}, axCfg);
      toast.success("Comisión marcada como pagada");
      // Refresh
      if (selectedAgent) {
        const res = await axios.get(`${API}/admin/agents/${selectedAgent.id}/commissions`, axCfg);
        setAgentCommissions(res.data);
      }
      fetchActivityData();
    } catch (e) {
      toast.error("Error al actualizar comisión");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const exportCommissionsToExcel = async (year = null, month = null) => {
    try {
      toast.info("Generando archivo Excel...");
      let url = `${API}/admin/commissions/export`;
      const params = new URLSearchParams();
      if (year) params.append("year", year);
      if (month) params.append("month", month);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        ...axCfg,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `comisiones_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success("Archivo Excel descargado");
    } catch (e) {
      toast.error("Error al exportar comisiones");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-[#103F38]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard de Actividad</h2>
          <p className="text-gray-500">Rendimiento de agentes y comisiones</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => exportCommissionsToExcel()}
            className="text-green-700 border-green-300 hover:bg-green-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-none shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{totals.totalAgents}</p>
                <p className="text-xs text-blue-600">Agentes/Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <Briefcase className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{totals.totalClients}</p>
                <p className="text-xs text-green-600">Clientes Creados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{totals.totalTramites}</p>
                <p className="text-xs text-purple-600">Trámites</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-700">{formatCurrency(totals.totalCommissions)}</p>
                <p className="text-xs text-amber-600">Comisiones Totales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-red-700">{formatCurrency(totals.pendingCommissions)}</p>
                <p className="text-xs text-red-600">Pendientes de Pago</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Activity Table */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#103F38]" />
            Actividad por Usuario
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activityData.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay datos de actividad</p>
          ) : (
            <div className="space-y-3">
              {activityData.map((item) => {
                const agent = item.agent;
                const stats = item.stats || {};
                const totalActivity = (stats.clients_created || 0) + (stats.tramites_count || 0) + (stats.prospects_count || 0);
                const maxActivity = Math.max(...activityData.map(a => 
                  (a.stats?.clients_created || 0) + (a.stats?.tramites_count || 0) + (a.stats?.prospects_count || 0)
                ));
                const activityPercent = maxActivity > 0 ? (totalActivity / maxActivity) * 100 : 0;
                
                return (
                  <div 
                    key={agent.id}
                    className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                    data-testid={`agent-activity-${agent.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          agent.role === "admin" ? "bg-purple-100" : "bg-[#103F38]/10"
                        }`}>
                          <span className={`text-sm font-bold ${
                            agent.role === "admin" ? "text-purple-700" : "text-[#103F38]"
                          }`}>
                            {(agent.name || agent.email || "?").charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{agent.name || agent.email}</h4>
                            <Badge className={agent.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                              {agent.role === "admin" ? "Admin" : "Agente"}
                            </Badge>
                            {!agent.is_active && (
                              <Badge className="bg-gray-100 text-gray-600">Inactivo</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{agent.email}</p>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCommissionsModal(agent)}
                        className="text-[#103F38]"
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Ver Comisiones
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-5 gap-4 mb-3">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{stats.prospects_count || 0}</p>
                        <p className="text-xs text-gray-500">Prospectos</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{stats.clients_created || 0}</p>
                        <p className="text-xs text-gray-500">Clientes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{stats.tramites_count || 0}</p>
                        <p className="text-xs text-gray-500">Trámites</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{formatCurrency(stats.total_commissions)}</p>
                        <p className="text-xs text-gray-500">Comisiones</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-amber-600">{formatCurrency(stats.pending_commissions)}</p>
                        <p className="text-xs text-gray-500">Pendientes</p>
                      </div>
                    </div>

                    {/* Activity Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Actividad relativa</span>
                        <span>{totalActivity} acciones</span>
                      </div>
                      <Progress value={activityPercent} className="h-2" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Commissions */}
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-[#103F38]" />
            Últimas Comisiones Generadas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {commissions.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay comisiones registradas</p>
          ) : (
            <div className="space-y-2">
              {commissions.slice(0, 10).map((comm) => (
                <div 
                  key={comm.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      comm.status === "paid" ? "bg-green-100" : "bg-amber-100"
                    }`}>
                      {comm.status === "paid" ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Clock className="h-4 w-4 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{comm.agent_name || comm.agent_email}</p>
                      <p className="text-xs text-gray-500">
                        {comm.tramite_title} - {comm.client_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#103F38]">{formatCurrency(comm.amount)}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Percent className="h-3 w-3" />
                      {comm.commission_rate}% de {formatCurrency(comm.base_amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Commissions Modal */}
      <Dialog open={showCommissionsModal} onOpenChange={setShowCommissionsModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#103F38]" />
              Comisiones de {selectedAgent?.name || selectedAgent?.email}
            </DialogTitle>
            <DialogDescription>
              Historial completo de comisiones generadas
            </DialogDescription>
          </DialogHeader>

          {agentCommissions ? (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-700">
                    {formatCurrency(agentCommissions.total_earned)}
                  </p>
                  <p className="text-xs text-green-600">Total Pagado</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-amber-700">
                    {formatCurrency(agentCommissions.total_pending)}
                  </p>
                  <p className="text-xs text-amber-600">Pendiente de Pago</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-700">
                    {agentCommissions.commissions?.length || 0}
                  </p>
                  <p className="text-xs text-blue-600">Total Comisiones</p>
                </div>
              </div>

              {/* Commission List */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-700">Historial</h4>
                {agentCommissions.commissions?.length === 0 ? (
                  <p className="text-center py-4 text-gray-500 text-sm">
                    Este agente no tiene comisiones registradas
                  </p>
                ) : (
                  agentCommissions.commissions?.map((comm) => (
                    <div 
                      key={comm.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-sm">{comm.tramite_title}</p>
                        <p className="text-xs text-gray-500">{comm.client_name}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(comm.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-[#103F38]">{formatCurrency(comm.amount)}</p>
                          <Badge className={comm.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                            {comm.status === "paid" ? "Pagada" : "Pendiente"}
                          </Badge>
                        </div>
                        {comm.status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => markCommissionPaid(comm.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Pagar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
